import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { StorageService } from './storage/storage.service';
import { PlatformService } from './platform.service';
import { UserLocalization } from '@shared/models/locale/locale.models';
import { environment } from '../../../environments/environment';

// Translations are imported (not fetched) so the first paint is never untranslated.
// They live under src/, not publicDir, so the bundler inlines them.
import enTranslations from '../../../i18n/en_US.json';
import ukTranslations from '../../../i18n/uk_UA.json';
import ruTranslations from '../../../i18n/ru_UA.json';

type TranslationObject = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly translations: Record<string, TranslationObject> = {
    en_US: enTranslations as TranslationObject,
    uk_UA: ukTranslations as TranslationObject,
    ru_UA: ruTranslations as TranslationObject,
  };

  private static readonly FALLBACK_LOCALE = UserLocalization.uk_UA;

  /** Where the last-known site languages are cached between visits. */
  private static readonly SITE_LOCALES_KEY = 'siteLocales';

  /**
   * The languages the site publishes — `site.site.locales`, set in the
   * backoffice's site settings, the same list the public site's switcher
   * shows. Until the read lands, the last list this browser saw, or English
   * alone on a first visit, so the picker never offers a language that is then
   * withdrawn a moment later.
   */
  private readonly siteLocales = signal<UserLocalization[]>([UserLocalization.en_US]);

  /** The languages the pickers offer. */
  public readonly supportedLocales = this.siteLocales.asReadonly();

  private readonly currentLocale = signal<UserLocalization>(TranslationService.FALLBACK_LOCALE);

  public readonly currentLocaleSignal = computed(() => this.currentLocale());

  private readonly storageService = inject(StorageService);
  private readonly platformService = inject(PlatformService);

  constructor() {
    if (!this.platformService.isBrowser()) {
      return;
    }

    const cached = this.parseLocales(this.storageService.getItem(TranslationService.SITE_LOCALES_KEY));
    if (cached.length) {
      this.siteLocales.set(cached);
    }
    void this.loadSiteLocales();

    const storedLocale = this.storageService.getItem('locale') as UserLocalization | null;
    if (storedLocale && this.isSupportedLocale(storedLocale)) {
      this.currentLocale.set(storedLocale);
    }

    effect(() => {
      const locale = this.currentLocale();
      this.storageService.setItem('locale', locale);
      window.dispatchEvent(new CustomEvent('locale-changed', { detail: { locale } }));
    });

    // Another tab changed the locale.
    window.addEventListener('storage', event => {
      if (event.key === 'locale' && event.newValue) {
        const newLocale = event.newValue as UserLocalization;
        if (this.isSupportedLocale(newLocale)) {
          this.currentLocale.set(newLocale);
        }
      }
    });
  }

  /** The languages the site publishes. See {@link supportedLocales}. */
  public getSupportedLocales(): UserLocalization[] {
    return this.siteLocales();
  }

  /**
   * Read the published languages.
   *
   * `fetch`, not `HttpClient`: the HTTP interceptors inject this service to
   * stamp the locale on every request, so asking for `HttpClient` here would
   * be a dependency cycle. The endpoint is public and needs none of them.
   *
   * A failure keeps whatever is already offered — the cached list or English —
   * rather than leaving the learner with no language at all.
   */
  private async loadSiteLocales(): Promise<void> {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/site_locales/`);
      if (!response.ok) {
        return;
      }
      const locales = this.parseLocales(JSON.stringify(await response.json()));
      if (!locales.length) {
        return;
      }
      this.siteLocales.set(locales);
      this.storageService.setItem(TranslationService.SITE_LOCALES_KEY, JSON.stringify(locales));
      // A language the site stopped publishing (ru_UA, say) cannot stay
      // selected: nothing would be authored in it.
      if (!locales.includes(this.currentLocale())) {
        this.currentLocale.set(
          locales.includes(TranslationService.FALLBACK_LOCALE)
            ? TranslationService.FALLBACK_LOCALE
            : locales[0]
        );
      }
    } catch {
      // Offline or API down: keep what is offered.
    }
  }

  /** Known codes only, in the order given; anything else is dropped. */
  private parseLocales(raw: string | null): UserLocalization[] {
    try {
      const list: unknown = raw ? JSON.parse(raw) : [];
      const known = Object.values(UserLocalization) as string[];
      return Array.isArray(list)
        ? (list.filter(code => typeof code === 'string' && known.includes(code)) as UserLocalization[])
        : [];
    } catch {
      return [];
    }
  }

  public getCurrentLocale(): UserLocalization {
    return this.currentLocale();
  }

  public setLocale(locale: UserLocalization): void {
    if (this.isSupportedLocale(locale)) {
      this.currentLocale.set(locale);
    }
  }

  /**
   * Resolve a dotted translation key (`FEATURE.SECTION.KEY`) for the active locale.
   * Falls back to the fallback locale, then to the key itself so a missing string
   * is visible in the UI rather than blank.
   */
  public instant(key: string | null | undefined): string {
    if (!key || typeof key !== 'string') {
      return '';
    }
    return this.resolve(key, this.currentLocale()) ?? key;
  }

  private resolve(key: string, locale: UserLocalization): string | null {
    const direct = this.lookup(key, this.translations[locale]);
    if (direct !== null) {
      return direct;
    }
    // A published language this app has no UI file for yet (hr_HR, el_GR)
    // reads in English rather than Ukrainian; course content still arrives in
    // the chosen language, since the server localises it.
    if (!this.translations[locale]) {
      return this.lookup(key, this.translations[UserLocalization.en_US]);
    }
    if (locale === TranslationService.FALLBACK_LOCALE) {
      return null;
    }
    return this.lookup(key, this.translations[TranslationService.FALLBACK_LOCALE]);
  }

  private lookup(key: string, translations: TranslationObject | undefined): string | null {
    let value: unknown = translations;
    for (const segment of key.split('.')) {
      if (value && typeof value === 'object' && segment in (value as TranslationObject)) {
        value = (value as TranslationObject)[segment];
      } else {
        return null;
      }
    }
    return typeof value === 'string' ? value : null;
  }

  private isSupportedLocale(locale: string): locale is UserLocalization {
    return this.getSupportedLocales().includes(locale as UserLocalization);
  }
}
